import { ApiState } from "../../common/constants/api-constants";
import { TaxInfoDetails } from "../tax-info/tax-info-options";

export enum StepId {
  ShippingAddressStep = 'shipping-address-checkout-step',
  ShippingMethodStep = 'shipping-method-checkout-step',
  PaymentBillingAddressStep = 'payment-billing-checkout-step'
}

export declare interface OrderRequest {
  order: Order;
  paymentDetails: PaymentDetails;
}

export declare interface MagentoOrderRequest {
  order: PaymentInformation;
  paymentDetails: PaymentDetails;
}

export interface PaymentInformation {
  paymentMethod: {
    method: PaymentTypeMagento;
    additional_data?: PaymentAdditionalData;
  };
}

export interface PaymentInformationGuestCheckout extends PaymentInformation {
  email: string;
}

interface PaymentAdditionalData {
  payment_method_nonce?: string;
  store_in_vault?: boolean;
  device_data?: string;
  unique_number_identifier?: string;
  three_d_secure_authentication_id?: string;
  payment_method?: PaymentTypeMagento;
  sfdc_address_id?: string;
  institution?: number;
  courseName?: string;
  courseNumber?: string;
  term?: string;
  year?: number;
  students?: number;
  authorText?: string;
  titleText?: string;
  decisionDate?: string;
}

export declare interface Order {
  countryCode: string;
  currencyCode: string;
  cartGuid?: string;
  totalAmount: number;
  tax: number;
  discount: number;
  freight: number;
  handling: number;
  serviceFees: number;
  promoCode?: string;
  billingAddress: RmsBookstoreAddress;
  shippingAddress: RmsBookstoreAddress;
  paymentType: PaymentType;
  poNumber?: string;
  shippingInfo?: ShippingInfo;
  erpAccountNumber?: string;
  venmoUserName?: string;
}

export declare interface CreateOrderResponse {
  orderId: string;
}

export declare interface OrderStatusResponse {
  content: OrderStatusDetails;
  success: boolean;
  message: string;
  code: number;
}

export declare interface ShippingInfo {
  shippingMethod: string;
  freightTerm: string;
  shipmentServiceLevel: string;
}

export declare interface ClientTokenDetails {
  token: string;
  threeDSecureEnabled: boolean;
  environment: BraintreeEnvironment;
  ThreeDSecure2Details?: ThreeDSecure2Details;
  paypalEnabled: boolean;
  vaultEnabled: boolean;
  googlePayEnabled: boolean;
  applePayEnabled: boolean;
  googleMerchantId?: string;
  vaultedCards?: VaultedCard[];
  venmoEnabled: boolean;
  venmoProfileId?: string;
  idealEnabled?: boolean;
  idealMerchantId?: string;
}

export declare interface ClientTokenDetailsMagento {
  token: string;
  three_d_secure_enabled: boolean;
  env: BraintreeEnvironment;
  paypal_enabled: boolean;
  google_pay: boolean;
  google_pay_merchant_id: string;
  apple_pay: boolean;
  vault_enabled: boolean;
  venmo_enabled: boolean;
  venmo_profile_id?: string;
  vaulted_cards?: VaultedCardsMagento[];
  ThreeDSecure2Details: ThreeDSecure2Details; //Haven't found this one neither on shop or magento
  venmoProfileId?: string;
  ideal?: boolean;
  ideal_merchant_id?: string;
}

export declare interface VaultedCardsMagento {
  card_type: string;
  card_last4: string;
  bin: string;
  unique_identifier: string;
  expired: boolean;
  default: boolean;
  expiry_year: string;
  expiry_month: string;
  card_holder_name: string;
  billing_address?: VaultedCardBillingAddessMagento;
}

export declare interface VaultedCardBillingAddessMagento {
  first_name: string;
  last_name: string;
  party_site_id: number;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
  county: string;
  country: string;
  rdi_type: string;
  validated: string;
  phone_number: string;
}

export declare interface ThreeDSecure2Details {
  amount: number;
  email: string;
  billingAddress: BillingAddressFor3ds;
}

export declare interface VaultedCard {
  cardType: string;
  cardLast4: string;
  bin: string;
  uniqueIdentifier: string;
  billingAddress?: RmsBookstoreAddress;
  isExpired: boolean;
  isDefault: boolean;
  expiryYear: string;
  expiryMonth: string;
  cardHolderName: string;
  hasBillingAddressChanged?: boolean;
}

export declare interface BillingAddressFor3ds {
  givenName: string;
  surname: string;
  phoneNumber: string;
  streetAddress: string;
  extendedAddress: string;
  locality: string;
  region: string;
  postalCode: string;
  countryCodeAlpha2: string;
}
type BraintreeEnvironment = BraintreeEnv.Sandbox | BraintreeEnv.Production;

export enum BraintreeEnv {
  Sandbox = "SANDBOX",
  Production = "PRODUCTION"
}

export enum OrderStatus {
  SUCCESS = 'SUCCESS', PENDING = 'PENDING', FAILURE = 'FAILURE'
}

export enum PaypalButtonLayout {
  VERTICAL = "vertical", HORIZONTAL = "horizontal"
}

export enum PaypalButtonColor {
  GOLD = "gold", BLUE = "blue", SILVER = "silver", WHITE = "white", BLACK = "black"
}

export enum PaypalButtonShape {
  RECTANGULAR = "rect", PILL = "pill"
}

export enum PaypalButtonLabel {
  PAYPAL = "paypal", CHECKOUT = "checkout", BUYNOW = "buynow", PAY = "pay", INSTALLMENT = "installment"
}

export declare interface ClientTokenRequest {
  currencyCode?: string;
  countryCode?: string;
  businessUnit?: BusinessUnit;
  type?: 'cart' | 'checkout';
  storeCode?: string;
}

export enum BusinessUnit {
  SEG = "SEG",
  HE = "HE",
  PROFESSIONAL = "PROFESSIONAL"
}

export declare interface RmsBookstoreAddress {
  firstName: string;
  lastName: string;
  partySiteId?: number;
  address1: string;
  address2?: string;
  unit?: string;
  address3?: string;
  address4?: string;
  city: string;
  state: string;
  zipCode: string;
  county?: string;
  country: string;
  rdiType?: string;
  validated?: string;
  phoneNumber: string;
  dpvMatchCode?: string;
  verificationStatus?: string;
  addressType?: string;
}

export declare interface AddressValidationInfo {
  address1: string;
  address2?: string;
  address3?: string;
  address4?: string;
  city: string;
  state: string;
  zipCode: string;
  county?: string;
  country: string;
  rdiType?: string;
  validated?: string;
  dpvMatchCode?: string;
  verificationStatus?: string;
  addressType?: string;
}
export declare interface FreightCalculationRequest {
  shippingAddress: RmsBookstoreAddress;
  promoCode?: string;
}
export declare interface RmsFreightCalcResponse {
  information: string;
  wareHouse: string;
  freightEstimateTypes: RmsFreightEstimateType[];
}

export declare interface RmsFreightEstimateType {
  shippingMethodCode: string;
  shipMethodDescription: string;
  carrier: string;
  freightPrice: RmsPriceType;
  handlingPrice: RmsPriceType;
  shipmentServiceLevel: string;
  modeOfTransport: string;
  freightTerm: string;
  freightType: string;
}

export declare interface RmsPriceType {
  value: number;
  currencyCode: string;
}
export declare interface SelectedShippingMethod {
  information: string;
  wareHouse: string;
  freightEstimateTypes: RmsFreightEstimateType;
}
export declare interface ClientTokenResponseState extends ApiState {
  data: ClientTokenDetails | undefined;
}

export declare interface FreightCalculationResponseState extends ApiState {
  data: RmsFreightCalcResponse | undefined;
}

export declare interface CreateOrderResponseState extends ApiState {
  data: CreateOrderResponse | undefined;
}

export declare interface OrderStatusResponseState extends ApiState {
  data: OrderStatusResponse | undefined;
}

export declare interface TaxCalculationResponseState extends ApiState {
  data: TaxResponse | undefined;
}

export declare interface CreditCardDisplay {
  cardholderName?: string;
  cardType: string;
  cardLast4: string;
  bin: string;
  expiryMonth: string;
  expiryYear: string;
}

export declare interface PaymentDetails {
  paymentType?: PaymentType;
  paymentNonce?: string;
  cvvNonce?: string;
  threedsAuthenticationId?: string;
  creditCardUniqueIdentifier?: string;
  creditCardDisplayInfo?: CreditCardDisplay;
  deviceData?: string;
  saveInVault?: boolean;
  updatePaymentMethodAddress?: boolean;
  paypalEmail?: string;
  thirdPartyDetails?: {
    cardType: string;
    lastDigits: string;
  };
  venmoUserName?: string;
}

export declare interface OrderCompletionDetails {
  orderId?: string;
}

export enum PaymentType {
  BRAINTREE_APPLE_PAY = 'BRAINTREE_APPLE_PAY',
  BRAINTREE_CREDIT_CARD = 'BRAINTREE',
  BRAINTREE_GOOGLE_PAY = 'BRAINTREE_GOOGLE_PAY',
  BRAINTREE_PAYPAL = 'BRAINTREE_PAYPAL',
  BRAINTREE_VENMO = 'BRAINTREE_VENMO',
  PURCHASE_ORDER = 'PURCHASEORDER',
  BRAINTREE_IDEAL = 'BRAINTREE_IDEAL',
}

export enum PaymentTypeMagento {
  ANDROID_PAY_CARD = 'android_pay_card',
  APPLE_PAY_CARD = 'apple_pay_card',
  BRAINTREE_APPLE_PAY = 'braintree_applepay',
  BRAINTREE_CREDIT_CARD = 'braintree',
  BRAINTREE_GOOGLE_PAY = 'braintree_googlepay',
  BRAINTREE_PAYPAL = 'braintree_paypal',
  BRAINTREE_PREPAID = 'braintree_prepaid',
  BRAINTREE_VENMO = 'braintree_venmo',
  FREE_TRIAL = 'free_trial',
  PURCHASE_ORDER = 'purchaseorder',
  FREE = 'free',
  BRAINTREE_LOCAL_PAYMENT = 'braintree_local_payment',
}

export enum PaymentTypeShop {
  BRAINTREE_CREDIT_CARD = 'BRAINTREE',
  BRAINTREE_PAYPAL = 'BRAINTREE_PAYPAL',
}

export const getPaymentTypeMagento = (paymentType: PaymentType) => {
  switch (paymentType) {
    case (PaymentType.BRAINTREE_APPLE_PAY): return PaymentTypeMagento.BRAINTREE_APPLE_PAY;
    case (PaymentType.BRAINTREE_CREDIT_CARD): return PaymentTypeMagento.BRAINTREE_CREDIT_CARD;
    case (PaymentType.BRAINTREE_GOOGLE_PAY): return PaymentTypeMagento.BRAINTREE_GOOGLE_PAY;
    case (PaymentType.BRAINTREE_PAYPAL): return PaymentTypeMagento.BRAINTREE_PAYPAL;
    case (PaymentType.BRAINTREE_VENMO): return PaymentTypeMagento.BRAINTREE_VENMO;
    case (PaymentType.PURCHASE_ORDER): return PaymentTypeMagento.PURCHASE_ORDER;
    case (PaymentType.BRAINTREE_IDEAL): return PaymentTypeMagento.BRAINTREE_LOCAL_PAYMENT;
  }
}

export enum BraintreeHostedFieldsStatus {
  VALID = "VALID",
  INVALID = "INVALID"
}

export declare interface Money {
  amount: number;
  currency?: string;
}
export declare interface TaxResponse {
  totalTax: Money;
  digitalHandlingFees: Money;
  digitalHandlingFeesIsbn?: string;
}

export declare interface TaxRequest {
  billingAddress: RmsBookstoreAddress;
  shippingAddress?: RmsBookstoreAddress;
  promoCode?: string;
  freightPrice?: number;
  handlingPrice?: number;
  selectedShipmentLevel?: string;
  cartGuid?: string;
}
export declare interface ShopCheckoutState {
  skipFreightCalculation: boolean;
  activeStepId?: StepId;
  disableNextStepButton: boolean;
  isComplete: boolean;
  checkoutInfoSubmitted: boolean;
  shippingAddress: RmsBookstoreAddress | undefined;
  freightCalculation: FreightCalculationResponseState;
  orderCreation: CreateOrderResponseState;
  orderStatusCheck: OrderStatusResponseState;
  enableOrderStatusReprocess: boolean;
  orderStatusFailureCount: number;
  selectedShippingMethod: SelectedShippingMethod | undefined;
  selectedPaymentType: PaymentType | undefined;
  clientToken: ClientTokenResponseState;
  braintreeHostedFieldsStatus: BraintreeHostedFieldsStatus | undefined;
  paymentDetails: PaymentDetails | undefined;
  lastPaypalDetail: PaymentDetails | undefined;
  processingPaypalInfo?: boolean;
  taxCalculation: TaxCalculationResponseState;
  billingAddress: RmsBookstoreAddress | undefined;
  applePayBillingAddress: RmsBookstoreAddress | undefined;
  googlePayBillingAddress: RmsBookstoreAddress | undefined;
  paypalBillingAddress: RmsBookstoreAddress | undefined;
  billingAddressSameAsShippingAddress: boolean;
  isShippingAddressFromAddressbook: boolean;
  currencyCode: string | undefined;
  countryCode: string | undefined;
  poOrderNumber: string | undefined;
  removedSavedCard: ApiState;
  editBillingAddressMode: boolean;
  orderCompletion: OrderCompletionDetails | undefined;
  taxInfo: TaxInfoDetails | undefined;
  removedPaymentMethod: string | undefined;
  purchaseOrderSelected: boolean;
  purchaseOrderApplied: boolean;
  purchaseOrderDetails: PurchaseOrderDetails | undefined;
  savePurchaseOrder: ApiState;
}

export declare interface RemoveCreditCardRequest {
  creditCardUniqueId: string;
  currencyCode: string;
  countryCode: string;
  businessUnit: BusinessUnit;
  updatedVaultedCardsList: VaultedCard[];
}

export declare interface GetShippingMethodMagentoRequest {
  address: MagentoBookstoreAddress;
}

export declare interface MagentoBookstoreAddress {
  region_code?: string;
  region?: string;
  country_id: string;
  street: string[];
  postcode: string;
  city: string;
  firstname: string;
  lastname: string;
  customer_id?: number;
  email?: string;
  telephone: string;
  same_as_billing?: number;
  custom_attributes?: MagentoBookstoreAddressCustomAttributes;
}

export declare interface MagentoBookstoreAddressCustomAttributes {
  erp_site_id?: number;
  rdi_type?: string;
  validation_flag?: string;
  county?: string;
  tax_name?: string;
  tax_id?: string;
  tax_postal_code?: string;
  tax_attribute1?: string;
  tax_attribute2?: string;
  tax_type_of_name?: string;
  tax_type_of_id?: string;
  tax_reason?: string;
  tax_type_of_document?: string;
  tax_municipality?: string;
  tax_region?: string;
  tax_representation?: string;
}

export declare interface SetShippingMethodMagentoRequest {
  addressInformation: {
    shipping_address: MagentoBookstoreAddress;
    billing_address: MagentoBookstoreAddress;
    shipping_carrier_code?: string;
    shipping_method_code?: string;
  };
}

export declare interface OrderStatusDetails {
  order_id?: number;
  rms_order_id?: string;
  status: OrderStatus;
  created_at: string;
  updated_at: string;
}

export declare interface PurchaseOrderDetails {
  purchase_order_number: string,
  is_purchase_order: boolean
}

export enum CardTypes {
  AMEX = 'American Express',
  APPLE_PAY = 'Apple Pay',
  DISCOVER = 'Discover',
  GOOGLE_PAY = 'Google Pay',
  MASTERCARD = 'MasterCard',
  PAYPAL = 'PayPal',
  VENMO = 'Venmo',
  VISA = 'Visa',
  IDEAL = 'iDEAL',
}
